import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FindingsScopeBar } from "@/components/findings-scope/findings-scope-bar";
import {
  FindingsPeriodSummary,
  PERIOD_BUCKET_PARAM,
} from "@/components/findings-scope/period-summary";
import {
  hasScope,
  migrateLegacyWindow,
  readStoredScope,
  scopeParams,
  storeScope,
} from "@/components/findings-scope/scope-url-state";
import {
  isHotspotTab,
  queueParams,
  withHotspotState,
} from "@/components/hotspots/hotspot-url-state";
import { HotspotsView } from "@/components/hotspots/hotspots-view";
import { PagesView } from "@/components/pages-view/pages-view";
import { Page } from "@/components/layout/page";
import { ViewTabs } from "@/components/layout/view-tabs";
import { FindingsQueue } from "@/components/triage/findings-queue";

/** Params owned by the Pages view (dropped when leaving it). */
const PAGES_PARAMS = ["route", "all"];

/** The period summary bucket filters the Queue only. */
const withoutBucket = (params: URLSearchParams) => {
  const p = new URLSearchParams(params);
  p.delete(PERIOD_BUCKET_PARAM);
  return p;
};

const withoutPagesParams = (params: URLSearchParams) => {
  const p = new URLSearchParams(params);
  for (const name of PAGES_PARAMS) p.delete(name);
  return p;
};

/**
 * Findings: the triage queue, the hotspot map (`?tab=hotspots`), or the
 * page view (`?tab=pages`), under one page-wide scope (period, persona,
 * capability, severity, lens) that every tab keeps.
 */
export function ProjectFindingsPage() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  useRememberedScope(id, params, setParams);
  if (!id) return null;

  const tabParam = params.get("tab");
  const isHotspot = isHotspotTab(params);
  const isPages = tabParam === "pages";
  const currentTab = isPages ? "pages" : isHotspot ? "hotspots" : "queue";

  // Switching keeps the scope; the queue also keeps its own filters.
  const queueQuery = queueParams(withoutPagesParams(params)).toString();
  const pagesQuery = scopeParams(params);
  pagesQuery.set("tab", "pages");

  return (
    <Page title="Findings">
      <div className="space-y-4">
        <FindingsScopeBar projectId={id} />
        <FindingsPeriodSummary projectId={id} />
        <ViewTabs
          label="Findings views"
          current={currentTab}
          tabs={[
            {
              id: "queue",
              label: "Queue",
              to: queueQuery ? `?${queueQuery}` : "?",
            },
            {
              id: "hotspots",
              label: "Hotspots",
              to: `?${withHotspotState(withoutBucket(withoutPagesParams(params)), {})}`,
            },
            {
              id: "pages",
              label: "Pages",
              to: `?${pagesQuery}`,
            },
          ]}
        />
        <div className="pt-1">
          {currentTab === "pages" ? (
            <PagesView projectId={id} />
          ) : currentTab === "hotspots" ? (
            <HotspotsView projectId={id} />
          ) : (
            <FindingsQueue projectId={id} />
          )}
        </div>
      </div>
    </Page>
  );
}

/**
 * Keep the scope across visits and in-page links: a URL without one gets
 * the project's remembered scope (into the URL, so it shows); old
 * `window=30d` hotspot links are rewritten. Changes are remembered by
 * `useFindingsScope`'s setter, so Reset really clears.
 */
function useRememberedScope(
  projectId: string | undefined,
  params: URLSearchParams,
  setParams: ReturnType<typeof useSearchParams>[1],
) {
  useEffect(() => {
    if (!projectId) return;
    const migrated = migrateLegacyWindow(params);
    const base = migrated ?? params;
    const stored = hasScope(base) ? null : readStoredScope(projectId);
    if (stored) {
      const next = new URLSearchParams(base);
      for (const [k, v] of stored) next.set(k, v);
      setParams(next, { replace: true });
    } else if (migrated) {
      setParams(migrated, { replace: true });
    } else {
      storeScope(projectId, params);
    }
  }, [projectId, params, setParams]);
}
